/**
 * Filtros avançados para produtos com design chip-based (estilo Linear/Attio).
 * Segue o mesmo padrão do PropertiesAdvancedFilters.tsx
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FilterChip } from '../../../crm/leads/components/FilterChip';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

export interface ProductsFilters {
  category?: string[];
  itemStatus?: string[];
  opportunityType?: string[];
  minPrice?: number;
  maxPrice?: number;
  hasVariations?: boolean;
  stockStatus?: string;
}

// ═══════════════════════════════════════════════════════
// FILTER OPTIONS
// ═══════════════════════════════════════════════════════

interface FilterOption {
  value: string;
  label: string;
}

const CATEGORY_OPTIONS: FilterOption[] = [
  { value: 'electronics', label: 'Eletrônicos' },
  { value: 'furniture', label: 'Móveis' },
  { value: 'clothing', label: 'Vestuário' },
  { value: 'food', label: 'Alimentos' },
  { value: 'books', label: 'Livros' },
  { value: 'toys', label: 'Brinquedos' },
  { value: 'sports', label: 'Esportes' },
  { value: 'beauty', label: 'Beleza' },
  { value: 'automotive', label: 'Automotivo' },
  { value: 'home_garden', label: 'Casa e Jardim' },
  { value: 'other', label: 'Outros' },
];

const ITEM_STATUS_OPTIONS: FilterOption[] = [
  { value: 'active', label: 'Ativo' },
  { value: 'inactive', label: 'Inativo' },
  { value: 'outofstock', label: 'Sem estoque' },
  { value: 'discontinued', label: 'Descontinuado' },
  { value: 'archived', label: 'Arquivado' },
];

const OPPORTUNITY_TYPE_OPTIONS: FilterOption[] = [
  { value: 'sale', label: 'Venda' },
  { value: 'rent', label: 'Aluguel' },
  { value: 'lease', label: 'Arrendamento' },
  { value: 'exchange', label: 'Permuta' },
];

const STOCK_STATUS_OPTIONS: FilterOption[] = [
  { value: 'in_stock', label: 'Em estoque' },
  { value: 'low_stock', label: 'Estoque baixo' },
  { value: 'out_of_stock', label: 'Sem estoque' },
];

const VARIATION_OPTIONS: FilterOption[] = [
  { value: 'true', label: 'Com variações' },
  { value: 'false', label: 'Sem variações' },
];

// ═══════════════════════════════════════════════════════
// FILTER DEFINITIONS & CATEGORIES
// ═══════════════════════════════════════════════════════

type FilterType = 'multiselect' | 'singleselect' | 'text' | 'range';

interface FilterDefinition {
  key: string;
  label: string;
  type: FilterType;
  options?: FilterOption[];
  placeholder?: string;
  minLabel?: string;
  maxLabel?: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  unit?: string;
}

const FILTER_DEFINITIONS: Record<string, FilterDefinition> = {
  category: {
    key: 'category',
    label: 'Categoria',
    type: 'multiselect',
    options: CATEGORY_OPTIONS,
    placeholder: 'Buscar categoria...',
  },
  itemStatus: {
    key: 'itemStatus',
    label: 'Status',
    type: 'multiselect',
    options: ITEM_STATUS_OPTIONS,
    placeholder: 'Buscar status...',
  },
  opportunityType: {
    key: 'opportunityType',
    label: 'Operação',
    type: 'multiselect',
    options: OPPORTUNITY_TYPE_OPTIONS,
    placeholder: 'Buscar operação...',
  },
  priceRange: {
    key: 'priceRange',
    label: 'Preço',
    type: 'range',
    minLabel: 'Mínimo',
    maxLabel: 'Máximo',
    minPlaceholder: 'R$ Mín',
    maxPlaceholder: 'R$ Máx',
    unit: 'R$',
  },
  stockStatus: {
    key: 'stockStatus',
    label: 'Estoque',
    type: 'singleselect',
    options: STOCK_STATUS_OPTIONS,
  },
  hasVariations: {
    key: 'hasVariations',
    label: 'Variações',
    type: 'singleselect',
    options: VARIATION_OPTIONS,
  },
};

interface FilterCategory {
  id: string;
  label: string;
  icon: IconName;
  filters: string[];
}

const FILTER_CATEGORIES: FilterCategory[] = [
  {
    id: 'classification',
    label: 'Classificação',
    icon: 'tag',
    filters: ['category', 'itemStatus', 'opportunityType'],
  },
  {
    id: 'inventory',
    label: 'Inventário',
    icon: 'package',
    filters: ['stockStatus', 'hasVariations'],
  },
  {
    id: 'value',
    label: 'Valor',
    icon: 'dollar-sign',
    filters: ['priceRange'],
  },
];

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

interface ActiveFilter {
  key: string;
  label: string;
  displayValue: string;
}

function formatPriceCompact(value: number): string {
  if (value >= 1000000) return `R$${(value / 1000000).toFixed(1).replace('.', ',')}M`;
  if (value >= 1000) return `R$${(value / 1000).toFixed(0)}mil`;
  return `R$${value}`;
}

function getActiveFilters(filters: ProductsFilters): ActiveFilter[] {
  const entries: ActiveFilter[] = [];

  const multiSelectKeys = ['category', 'itemStatus', 'opportunityType'] as const;
  for (const key of multiSelectKeys) {
    const value = filters[key];
    if (!value || !Array.isArray(value) || value.length === 0) continue;
    const def = FILTER_DEFINITIONS[key];
    if (!def?.options) continue;

    const labels = value
      .map(v => def.options!.find(o => o.value === v)?.label)
      .filter(Boolean) as string[];
    if (labels.length === 0) continue;
    const displayValue = labels.length <= 2
      ? labels.join(', ')
      : `${labels[0]}, +${labels.length - 1}`;
    entries.push({ key, label: def.label, displayValue });
  }

  if (filters.stockStatus) {
    const option = STOCK_STATUS_OPTIONS.find(o => o.value === filters.stockStatus);
    entries.push({ key: 'stockStatus', label: 'Estoque', displayValue: option?.label || filters.stockStatus });
  }

  if (filters.hasVariations !== undefined) {
    entries.push({
      key: 'hasVariations',
      label: 'Variações',
      displayValue: filters.hasVariations ? 'Com variações' : 'Sem variações',
    });
  }

  if (filters.minPrice || filters.maxPrice) {
    let displayValue = '';
    if (filters.minPrice && filters.maxPrice) {
      displayValue = `${formatPriceCompact(filters.minPrice)} - ${formatPriceCompact(filters.maxPrice)}`;
    } else if (filters.minPrice) {
      displayValue = `a partir de ${formatPriceCompact(filters.minPrice)}`;
    } else if (filters.maxPrice) {
      displayValue = `até ${formatPriceCompact(filters.maxPrice)}`;
    }
    entries.push({ key: 'priceRange', label: 'Preço', displayValue });
  }

  return entries;
}

// ═══════════════════════════════════════════════════════
// PROPS
// ═══════════════════════════════════════════════════════

export interface ProductsAdvancedFiltersProps {
  filters: ProductsFilters;
  onFiltersChange: (filters: ProductsFilters) => void;
}

// ═══════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════

export const ProductsAdvancedFilters: React.FC<ProductsAdvancedFiltersProps> = ({
  filters,
  onFiltersChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<'categories' | 'values'>('categories');
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [optionSearchQuery, setOptionSearchQuery] = useState('');

  const [localMultiValues, setLocalMultiValues] = useState<string[]>([]);
  const [localRangeMin, setLocalRangeMin] = useState('');
  const [localRangeMax, setLocalRangeMax] = useState('');

  const triggerRef = useRef<HTMLButtonElement>(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  const activeFilters = useMemo(() => getActiveFilters(filters), [filters]);
  const hasFilters = activeFilters.length > 0;
  const currentFilterDef = selectedFilter ? FILTER_DEFINITIONS[selectedFilter] : null;

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }
  }, [isOpen]);

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return FILTER_CATEGORIES;
    const query = searchQuery.toLowerCase();
    return FILTER_CATEGORIES
      .map(cat => ({
        ...cat,
        filters: cat.filters.filter(fKey => {
          const def = FILTER_DEFINITIONS[fKey];
          return def?.label.toLowerCase().includes(query);
        }),
      }))
      .filter(cat => cat.filters.length > 0);
  }, [searchQuery]);

  const filteredOptions = useMemo(() => {
    if (!currentFilterDef?.options) return [];
    if (!optionSearchQuery.trim()) return currentFilterDef.options;
    const query = optionSearchQuery.toLowerCase();
    return currentFilterDef.options.filter(o =>
      o.label.toLowerCase().includes(query)
    );
  }, [currentFilterDef, optionSearchQuery]);

  const getFilterValue = (filterKey: string): unknown => {
    switch (filterKey) {
      case 'priceRange': return filters.minPrice || filters.maxPrice ? { min: filters.minPrice, max: filters.maxPrice } : undefined;
      case 'stockStatus': return filters.stockStatus;
      case 'hasVariations': return filters.hasVariations;
      default: return filters[filterKey as keyof ProductsFilters];
    }
  };

  const loadLocalValues = (filterKey: string) => {
    const def = FILTER_DEFINITIONS[filterKey];
    if (!def) return;

    switch (def.type) {
      case 'multiselect': {
        const value = filters[filterKey as keyof ProductsFilters];
        setLocalMultiValues(
          Array.isArray(value) ? [...value as string[]] : value ? [value as string] : []
        );
        break;
      }
      case 'singleselect': {
        if (filterKey === 'stockStatus') {
          setLocalMultiValues(filters.stockStatus ? [filters.stockStatus] : []);
        } else if (filterKey === 'hasVariations') {
          setLocalMultiValues(filters.hasVariations !== undefined ? [String(filters.hasVariations)] : []);
        }
        break;
      }
      case 'range': {
        if (filterKey === 'priceRange') {
          setLocalRangeMin(filters.minPrice ? String(filters.minPrice) : '');
          setLocalRangeMax(filters.maxPrice ? String(filters.maxPrice) : '');
        }
        break;
      }
    }
  };

  const handleOpenAdd = () => {
    setStep('categories');
    setSelectedFilter(null);
    setSearchQuery('');
    setOptionSearchQuery('');
    setLocalMultiValues([]);
    setLocalRangeMin('');
    setLocalRangeMax('');
    setIsOpen(true);
  };

  const handleEditChip = (filterKey: string) => {
    setStep('values');
    setSelectedFilter(filterKey);
    setOptionSearchQuery('');
    loadLocalValues(filterKey);
    setIsOpen(true);
  };

  const handleSelectCategory = (filterKey: string) => {
    setSelectedFilter(filterKey);
    setOptionSearchQuery('');
    loadLocalValues(filterKey);
    setStep('values');
  };

  const handleApply = () => {
    if (!selectedFilter) return;
    const def = FILTER_DEFINITIONS[selectedFilter];
    if (!def) return;

    const newFilters = { ...filters };

    switch (def.type) {
      case 'multiselect': {
        if (localMultiValues.length === 0) {
          delete newFilters[selectedFilter as keyof ProductsFilters];
        } else {
          (newFilters as Record<string, unknown>)[selectedFilter] = localMultiValues;
        }
        break;
      }
      case 'range': {
        if (selectedFilter === 'priceRange') {
          if (localRangeMin) newFilters.minPrice = Number(localRangeMin);
          else delete newFilters.minPrice;
          if (localRangeMax) newFilters.maxPrice = Number(localRangeMax);
          else delete newFilters.maxPrice;
        }
        break;
      }
    }

    onFiltersChange(newFilters);
    setIsOpen(false);
  };

  const handleSingleSelectValue = (value: string) => {
    if (!selectedFilter) return;
    const newFilters = { ...filters };

    if (selectedFilter === 'stockStatus') {
      if (filters.stockStatus === value) delete newFilters.stockStatus;
      else newFilters.stockStatus = value;
    } else if (selectedFilter === 'hasVariations') {
      const boolVal = value === 'true';
      if (filters.hasVariations === boolVal) delete newFilters.hasVariations;
      else newFilters.hasVariations = boolVal;
    }

    onFiltersChange(newFilters);
    setIsOpen(false);
  };

  const handleRemoveChip = (filterKey: string) => {
    const newFilters = { ...filters };

    switch (filterKey) {
      case 'priceRange':
        delete newFilters.minPrice;
        delete newFilters.maxPrice;
        break;
      default:
        delete newFilters[filterKey as keyof ProductsFilters];
    }

    onFiltersChange(newFilters);
  };

  const handleClearAll = () => {
    onFiltersChange({});
  };

  const handleToggleOption = (value: string) => {
    if (localMultiValues.includes(value)) {
      setLocalMultiValues(localMultiValues.filter(v => v !== value));
    } else {
      setLocalMultiValues([...localMultiValues, value]);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleBack = () => {
    setStep('categories');
    setSelectedFilter(null);
    setSearchQuery('');
  };

  const isFilterActive = (filterKey: string): boolean => {
    const value = getFilterValue(filterKey);
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {activeFilters.map((filter) => (
        <FilterChip
          key={filter.key}
          label={filter.label}
          value={filter.displayValue}
          onClick={() => handleEditChip(filter.key)}
          onRemove={() => handleRemoveChip(filter.key)}
        />
      ))}

      <button
        ref={triggerRef}
        type="button"
        onClick={handleOpenAdd}
        data-testid="products-filter-add"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200"
        style={{
          backgroundColor: 'var(--theme-button-ghost)',
          color: 'var(--theme-header-text)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--theme-header-button-hover)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost)';
        }}
      >
        <Icon name="plus" className="w-3.5 h-3.5" />
        <span>Filtro</span>
      </button>

      {hasFilters && (
        <button
          type="button"
          onClick={handleClearAll}
          data-testid="products-filter-clear"
          className="text-xs font-medium px-2 py-1.5 rounded transition-colors"
          style={{ color: 'var(--theme-muted-text)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--theme-header-text)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--theme-muted-text)';
          }}
        >
          Limpar
        </button>
      )}

      {isOpen &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[100]"
              onClick={handleClose}
            />

            <div
              className="fixed z-[101] w-72 rounded-lg shadow-xl flex flex-col"
              data-testid="products-filter-popover"
              style={{
                top: `${popoverPosition.top}px`,
                left: `${popoverPosition.left}px`,
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                maxHeight: '420px',
              }}
            >
              {step === 'categories' ? (
                <>
                  <div
                    className="p-3 flex-shrink-0"
                    style={{ borderBottom: '1px solid #e5e7eb' }}
                  >
                    <div className="relative">
                      <Icon name="search" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
                        style={{ color: '#9ca3af' }} />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        data-testid="products-filter-search"
                        placeholder="Buscar filtro..."
                        className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border h-[38px]"
                        style={{
                          backgroundColor: '#f9fafb',
                          borderColor: '#e5e7eb',
                          color: '#1e293b',
                        }}
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="overflow-y-auto flex-1 py-1">
                    {filteredCategories.length === 0 ? (
                      <div
                        className="text-center py-6 text-sm"
                        style={{ color: '#9ca3af' }}
                      >
                        Nenhum filtro encontrado
                      </div>
                    ) : (
                      filteredCategories.map((category, catIndex) => (
                        <div key={category.id}>
                          <div
                            className="flex items-center gap-2 px-3 py-1.5"
                            style={{
                              color: '#9ca3af',
                              ...(catIndex > 0
                                ? {
                                    borderTop: '1px solid #f3f4f6',
                                    marginTop: '4px',
                                    paddingTop: '8px',
                                  }
                                : {}),
                            }}
                          >
                            <Icon name={category.icon} className="w-3.5 h-3.5" style={{ color: '#9ca3af' }} />
                            <span className="text-[11px] font-semibold uppercase tracking-wider">
                              {category.label}
                            </span>
                          </div>

                          {category.filters.map((filterKey) => {
                            const def = FILTER_DEFINITIONS[filterKey];
                            if (!def) return null;
                            const active = isFilterActive(filterKey);

                            return (
                              <button
                                key={filterKey}
                                type="button"
                                onClick={() => handleSelectCategory(filterKey)}
                                data-testid={`products-filter-option-${filterKey}`}
                                className="w-full flex items-center justify-between py-2 text-sm transition-colors text-left"
                                style={{
                                  paddingLeft: '2rem',
                                  paddingRight: '0.75rem',
                                  color: '#1e293b',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#f9fafb';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                              >
                                <span>{def.label}</span>
                                {active && (
                                  <div
                                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: '#8b5cf6' }}
                                  />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      ))
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div
                    className="flex items-center gap-2 px-3 py-2.5 flex-shrink-0"
                    style={{ borderBottom: '1px solid #e5e7eb' }}
                  >
                    <button
                      type="button"
                      onClick={handleBack}
                      data-testid="products-filter-back"
                      className="p-1 rounded transition-colors"
                      style={{ color: '#64748b' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f1f5f9';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <Icon name="chevron-left" className="w-4 h-4" />
                    </button>
                    <span
                      className="text-sm font-semibold"
                      style={{ color: '#1e293b' }}
                    >
                      {currentFilterDef?.label}
                    </span>
                  </div>

                  <div className="overflow-y-auto flex-1">
                    {currentFilterDef?.type === 'multiselect' && (
                      <>
                        {(currentFilterDef.options?.length || 0) > 5 && (
                          <div className="px-3 pt-3 pb-1">
                            <div className="relative">
                              <Icon name="search" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
                                style={{ color: '#9ca3af' }} />
                              <input
                                type="text"
                                value={optionSearchQuery}
                                onChange={(e) => setOptionSearchQuery(e.target.value)}
                                data-testid="products-filter-option-search"
                                placeholder={currentFilterDef.placeholder || 'Buscar...'}
                                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border h-[38px]"
                                style={{
                                  backgroundColor: '#f9fafb',
                                  borderColor: '#e5e7eb',
                                  color: '#1e293b',
                                }}
                                autoFocus
                              />
                            </div>
                          </div>
                        )}

                        <div className="p-2 space-y-0.5">
                          {filteredOptions.length === 0 ? (
                            <div
                              className="text-center py-4 text-sm"
                              style={{ color: '#9ca3af' }}
                            >
                              Nenhuma opção encontrada
                            </div>
                          ) : (
                            filteredOptions.map((option) => {
                              const isSelected = localMultiValues.includes(option.value);

                              return (
                                <button
                                  key={option.value}
                                  type="button"
                                  onClick={() => handleToggleOption(option.value)}
                                  data-testid={`products-filter-value-${option.value}`}
                                  className="w-full flex items-center justify-between px-3 py-2 rounded text-sm transition-colors text-left"
                                  style={{
                                    backgroundColor: isSelected ? '#f5f3ff' : 'transparent',
                                    color: '#1e293b',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = isSelected ? '#f5f3ff' : 'transparent';
                                  }}
                                >
                                  <span>{option.label}</span>
                                  {isSelected && (
                                    <div
                                      className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                                      style={{ backgroundColor: '#8b5cf6' }}
                                    >
                                      <Icon name="check" className="w-3 h-3 text-white" />
                                    </div>
                                  )}
                                </button>
                              );
                            })
                          )}
                        </div>

                        {localMultiValues.length > 0 && (
                          <div
                            className="px-3 py-1.5 text-[11px]"
                            style={{ color: '#9ca3af' }}
                          >
                            {localMultiValues.length} selecionado{localMultiValues.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </>
                    )}

                    {currentFilterDef?.type === 'singleselect' && (
                      <div className="p-2 space-y-0.5">
                        {currentFilterDef.options?.map((option) => {
                          let isSelected = false;
                          if (selectedFilter === 'stockStatus') {
                            isSelected = filters.stockStatus === option.value;
                          } else if (selectedFilter === 'hasVariations') {
                            isSelected = filters.hasVariations === (option.value === 'true');
                          }

                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => handleSingleSelectValue(option.value)}
                              data-testid={`products-filter-value-${option.value}`}
                              className="w-full flex items-center justify-between px-3 py-2 rounded text-sm transition-colors text-left"
                              style={{
                                backgroundColor: isSelected ? '#f5f3ff' : 'transparent',
                                color: '#1e293b',
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = isSelected ? '#f5f3ff' : 'transparent';
                              }}
                            >
                              <span>{option.label}</span>
                              {isSelected && (
                                <div
                                  className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                                  style={{ backgroundColor: '#8b5cf6' }}
                                >
                                  <Icon name="check" className="w-3 h-3 text-white" />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {currentFilterDef?.type === 'range' && (
                      <div className="p-3 space-y-3">
                        <div>
                          <label
                            className="block text-xs font-medium mb-1"
                            style={{ color: '#64748b' }}
                          >
                            {currentFilterDef.minLabel || 'Mínimo'}
                          </label>
                          <input
                            type="number"
                            value={localRangeMin}
                            onChange={(e) => setLocalRangeMin(e.target.value)}
                            data-testid="products-filter-range-min"
                            placeholder={currentFilterDef.minPlaceholder || 'Mínimo'}
                            className="w-full px-3 py-2 text-sm rounded-md border h-[38px]"
                            style={{
                              backgroundColor: '#ffffff',
                              borderColor: '#d1d5db',
                              color: '#1e293b',
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = '#8b5cf6';
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor = '#d1d5db';
                            }}
                            autoFocus
                          />
                        </div>
                        <div>
                          <label
                            className="block text-xs font-medium mb-1"
                            style={{ color: '#64748b' }}
                          >
                            {currentFilterDef.maxLabel || 'Máximo'}
                          </label>
                          <input
                            type="number"
                            value={localRangeMax}
                            onChange={(e) => setLocalRangeMax(e.target.value)}
                            data-testid="products-filter-range-max"
                            placeholder={currentFilterDef.maxPlaceholder || 'Máximo'}
                            className="w-full px-3 py-2 text-sm rounded-md border h-[38px]"
                            style={{
                              backgroundColor: '#ffffff',
                              borderColor: '#d1d5db',
                              color: '#1e293b',
                            }}
                            onFocus={(e) => {
                              e.currentTarget.style.borderColor = '#8b5cf6';
                            }}
                            onBlur={(e) => {
                              e.currentTarget.style.borderColor = '#d1d5db';
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {currentFilterDef?.type !== 'singleselect' && (
                    <div
                      className="px-3 py-2.5 flex items-center justify-end flex-shrink-0"
                      style={{ borderTop: '1px solid #e5e7eb' }}
                    >
                      <button
                        type="button"
                        onClick={handleApply}
                        data-testid="products-filter-apply"
                        className="px-4 py-1.5 text-sm font-medium rounded-md transition-colors"
                        style={{
                          backgroundColor: '#8b5cf6',
                          color: '#ffffff',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#7c3aed';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#8b5cf6';
                        }}
                      >
                        Aplicar
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>,
          document.body
        )}
    </div>
  );
};
